/**
 * Glassbox Event Tracking Utilities
 * 
 * Simple event tracking for Glassbox using official JavaScript Detector API
 * Usage: glassboxEvent('button_click', { button_name: 'Submit', page: 'initiatives' });
 */

interface GlassboxEventData {
  [key: string]: any;
}

interface GlassboxDetector {
  triggerCustomEvent: (eventName: string, eventValue: string, domElement?: HTMLElement) => EventReturnObject;
  triggerCustomEventMap: (eventName: string, eventMap: Record<string, any>, domElement?: HTMLElement) => EventReturnObject;
}

interface EventReturnObject {
  status: 'success' | 'failure';
  message?: string;
}

/**
 * Automated/test accounts whose activity must never reach Glassbox.
 *
 * eis_modules_admin@optum.com is a synthetic monitor that hits the app every 10 minutes,
 * so its traffic looks like recurring real usage and skews every usage metric. Kept in
 * sync with the Hospital Quality and Physician Review modules, which exclude the same
 * account. Compared lower-cased, so entries here must be lower-case.
 */
const EXCLUDED_USER_EMAILS = ['eis_modules_admin@optum.com'];

/**
 * Once true, every Glassbox event is dropped for the rest of the session.
 * Set by GlassboxInitializer as soon as the signed-in user is known.
 */
let trackingSuppressed = false;

/**
 * Window flag mirroring `trackingSuppressed`, read by shared component libraries that
 * emit their own Glassbox events. It has to be a window flag because those libraries
 * are separate bundles with separate module scopes and cannot see the variable above.
 */
const SHARED_SUPPRESSION_FLAG = '__glassboxTrackingSuppressed';

/**
 * Whether this account's activity should be kept out of Glassbox entirely.
 */
export const isExcludedUserEmail = (email?: string | null): boolean =>
  !!email && EXCLUDED_USER_EMAILS.includes(email.trim().toLowerCase());

/**
 * Drop all Glassbox events for the remainder of this session - user identification,
 * button clicks and any other custom event, in this app and in shared components.
 */
export const suppressGlassboxTracking = (): void => {
  trackingSuppressed = true;
  if (typeof window !== 'undefined') {
    (window as any)[SHARED_SUPPRESSION_FLAG] = true;
  }
};

/**
 * Exposed for tests; production code never needs to un-suppress.
 */
export const resetGlassboxTrackingSuppression = (): void => {
  trackingSuppressed = false;
  if (typeof window !== 'undefined') {
    delete (window as any)[SHARED_SUPPRESSION_FLAG];
  }
};

export const isGlassboxTrackingSuppressed = (): boolean =>
  trackingSuppressed ||
  (typeof window !== 'undefined' && !!(window as any)[SHARED_SUPPRESSION_FLAG]);

/**
 * Send a custom event to Glassbox using official JavaScript Detector API
 * Uses triggerCustomEventMap to report key-value pairs
 * Similar to: gtag('event', 'screen_view', { app_name: 'myApp', screen_name: 'Home' });
 * 
 * @param eventName - Name of the event (e.g., 'button_click', 'form_submit', 'page_view')
 * @param eventData - Additional event data as key-value pairs (only primitives allowed)
 * @param domElement - Optional HTML element to associate with the event
 * 
 * @example
 * glassboxEvent('initiative_created', {
 *   initiative_name: 'Reduce Readmissions',
 *   facility: 'Hospital A',
 *   created_by: 'user123'
 * });
 */
export const glassboxEvent = (
  eventName: string, 
  eventData?: GlassboxEventData,
  domElement?: HTMLElement
): void => {
  
  try {
    // Excluded/automated accounts are never reported, not even anonymously
    if (isGlassboxTrackingSuppressed()) {
      return;
    }

    // Check if Glassbox detector is loaded
    const detector = (window as any)._detector as GlassboxDetector | undefined;

    if (!detector || typeof detector.triggerCustomEventMap !== 'function') {
      console.warn(`[Glassbox] Cannot send event '${eventName}': Detector not loaded or triggerCustomEventMap not available`);
      return;
    }

    // Get moduleInfo from window (set by App.tsx)
    const moduleInfo = (window as any).moduleInfo || {};

    // Build event map with automatic enrichment
    const eventMap: Record<string, any> = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    // Call official Glassbox API
    const result = detector.triggerCustomEventMap(eventName, eventMap, domElement);
  } catch (error) {
    console.error(`[Glassbox] Error sending event '${eventName}':`, error);
  }
};

/**
 * Track button click events with Glassbox
 * Common utility for tracking all button interactions across the application
 * 
 * The button name can include context information (e.g., 'Filters - Apply', 'Cases Flyout - Export as CSV')
 * to provide better insight into where the button was clicked.
 * 
 * @param buttonName - Descriptive name of the button with optional context prefix (e.g., 'Create Initiative', 'Filters - Apply')
 * @param additionalData - Optional additional data to track with the button click (can include context_header field)
 * @param domElement - Optional HTML element reference for the button
 * 
 * @example
 * // Simple button click
 * trackButtonClick('Create Initiative');
 * 
 * @example
 * // With context prefix
 * trackButtonClick('Filters - Apply');
 * 
 * @example
 * // With additional context
 * trackButtonClick('Save Filter', { 
 *   filter_name: 'My Custom Filter',
 *   filter_type: 'facility',
 *   context_header: 'Filters'
 * });
 * 
 * @example
 * // With DOM element for React onClick handlers
 * const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
 *   trackButtonClick('Submit Form', { form_type: 'initiative' }, event.currentTarget);
 *   // ... rest of your logic
 * };
 */
export const trackButtonClick = (
  buttonName: string,
  additionalData?: GlassboxEventData,
  domElement?: HTMLElement
): void => {
  glassboxEvent('button_click', {
    button_name: buttonName,
    ...additionalData
  }, domElement);
};

// TypeScript declarations
declare global {
  interface Window {
    _detector?: GlassboxDetector;
    _glassboxUserSet?: boolean;
    _glassboxConfigLoaded?: boolean;
    _glb_cd?: Record<string, any>;
  }
}